import React, { useEffect, useState } from 'react';
import { http_get as hubGet } from '../../utils/hub_client';
import { http_get as adminGet } from '../../utils/api_client';
import { HUB_ENDPOINTS, API_ENDPOINTS } from '../../utils/endpoints';
import './ChecklistLearner.scss';

// Normalize either the hub payload (learner: has per-item status + progress) or
// the admin payload (preview: draft items, no status) into one shape.
const normalize = (payload) => {
  const items = (payload.items || []).map((it) => ({ ...it, status: it.status || 'not_started' }));
  let progress = payload.progress;
  if (!progress) {
    const total = items.length;
    const completed = items.filter((i) => i.status === 'completed').length;
    const required = items.filter((i) => i.required);
    progress = {
      total,
      completed,
      required_total: required.length,
      required_completed: required.filter((i) => i.status === 'completed').length,
      complete: false
    };
  }
  return { ...payload, items, progress };
};

// BL-085 — learner-facing checklist page. Mounted by hub/show for a checklist
// material. Themed hero + overall progress + per-item cards with a five-state
// status pill and a Start/Continue CTA that opens the item's own experience.
const typeMeta = (t) => {
  switch ((t || '').toLowerCase()) {
    case 'walkthrough': return { cls: 'walk', label: 'Walkthrough' };
    case 'docs':
    case 'knowledge': return { cls: 'kb', label: 'Knowledge base' };
    case 'paths':
    default: return { cls: 'path', label: 'Path' };
  }
};

const STATUS = {
  completed: { cls: 'cp', label: 'Completed', cta: 'Review' },
  in_progress: { cls: 'ip', label: 'In progress', cta: 'Continue' },
  not_started: { cls: 'ns', label: 'Not started', cta: 'Start' }
};

const ShowPage = ({ id, audience = {}, preview = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const companySlug = window.location.pathname.split('/')[1];

  useEffect(() => {
    let cancelled = false;
    // Preview reads the live draft via the admin endpoint (no publish needed);
    // the real learner view reads the published snapshot via the hub endpoint.
    const request = preview
      ? adminGet(API_ENDPOINTS.GET_CHECKLIST(id))
      : hubGet(HUB_ENDPOINTS.GET_CHECKLIST(id));
    request
      .then(({ data: payload }) => {
        if (cancelled) return;
        if (payload?.error) setError(true); else setData(normalize(payload));
      })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id, preview]);

  if (loading) return <div className="cll-load">Loading your plan…</div>;
  if (error || !data) return <div className="cll-load">This checklist isn’t available.</div>;

  const items = data.items || [];
  const progress = data.progress || { total: 0, completed: 0 };
  const firstName = data.learner?.first_name || audience?.first_name;
  const heading = firstName ? `Welcome, ${firstName} — here’s your plan` : (data.name || 'Your plan');

  const openItem = (slug) => { if (slug) window.location.href = `/${companySlug}/hub/${slug}`; };
  const segWidth = `${100 / Math.max(items.length, 1)}%`;

  return (
    <div className="cll">
      {preview && <div className="cll-previewbar">Preview — draft. Publish to share this with learners.</div>}
      <header className="cll-hero">
        <div className="cll-hero__k">{data.header_kicker || 'Your onboarding plan'}</div>
        <h1 className="cll-hero__t">{heading}</h1>
      </header>

      <div className="cll-body">
        <div className="cll-wrap">
          <div className="cll-prog">
            <div className="cll-prog__c"><b>{progress.completed}</b> of <b>{progress.total}</b> items complete</div>
            <div className="cll-bar">
              {items.map((it, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <i key={i} className={`seg ${STATUS[it.status]?.cls || 'ns'}`} style={{ width: segWidth }} />
              ))}
            </div>
          </div>

          {items.length === 0 && <div className="cll-empty">This plan doesn’t have any items yet.</div>}

          {items.map((it) => {
            const meta = typeMeta(it.material_type);
            const st = STATUS[it.status] || STATUS.not_started;
            return (
              <div key={it.learning_material_id} className="cll-item">
                <span className={`cll-ic ${meta.cls}`} aria-hidden>{meta.label[0]}</span>
                <div className="cll-item__b">
                  <div className="cll-item__t">
                    {it.name}
                    {it.required && <span className="cll-req">Required</span>}
                  </div>
                  <div className="cll-item__m">{meta.label}</div>
                </div>
                <span className={`cll-pill ${st.cls}`}><i className="d" />{st.label}</span>
                <button type="button" className="cll-cta" onClick={() => openItem(it.slug)}>{st.cta}</button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ShowPage;
